import { useRef, type KeyboardEvent } from 'react';
import { darkTheme, FONT } from '../../theme/tokens';
import { boardCanvasView, type CanvasView } from '../../types';
import { boardTabId, boardTabPanelId, catalogTabId, catalogTabPanelId } from './catalogTabMeta';
import { CANVAS_NAV_SECTIONS, viewToTab } from './canvasNav';
import type { SelectableCoreView } from '../../store/workspaceShellStore';

const t = darkTheme;

/** One pinned board, resolved to what the bar needs. */
export interface BoardTabItem {
  id: string;
  title: string;
}

interface CanvasViewTabsProps {
  /** Active section, driven by the URL-derived view (see CatalogLayout). */
  activeView: CanvasView;
  /** Display label for the entities section — the catalog's entity term,
   *  capitalized (e.g. "Products", "Athletes"). */
  entityLabel: string;
  /** Core views currently selected onto the bar (SC-2873) — the entities
   *  anchor is always present and not listed here. */
  coreViewsInUse: readonly SelectableCoreView[];
  /** Boards pinned to the bar, in pin order. */
  boards: readonly BoardTabItem[];
  onNavigate: (view: CanvasView) => void;
  /** Open the View Library (the `+` — one door for workspace management). */
  onOpenLibrary: () => void;
}

interface TabEntry {
  view: CanvasView;
  label: string;
  glyph: string;
  tabId: string;
  panelId: string;
  /** Board tabs get the pinboard accent on their glyph. */
  board: boolean;
}

/**
 * Canvas section nav (SC-2655 → SC-2669 → SC-2873) — the catalog view switcher.
 * Since SC-2873 the bar is a SELECTION from the View Library: the entities
 * anchor + whichever core views are in use + pinned boards, with the `+` at the
 * end opening the library (data surfaces stay calm; management lives behind
 * that one door). Keeps the WAI-ARIA tablist contract — core sections' panel
 * ids stay keyed by `CatalogTab` via `viewToTab`; board tabs use the dynamic
 * `boardTabId`/`boardTabPanelId` pair — with roving tabindex + arrow keys
 * across every tab, board or core. The `+` sits inside the pill but OUTSIDE
 * the tablist (it opens a dialog; it isn't a tab), reached by Tab.
 */
export function CanvasViewTabs({
  activeView,
  entityLabel,
  coreViewsInUse,
  boards,
  onNavigate,
  onOpenLibrary,
}: CanvasViewTabsProps) {
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const entries: TabEntry[] = [
    ...CANVAS_NAV_SECTIONS.filter(
      (section) => section.view === 'entities' || coreViewsInUse.includes(section.view as SelectableCoreView),
    ).map((section) => ({
      view: section.view as CanvasView,
      label: section.label ?? entityLabel,
      glyph: section.glyph,
      tabId: catalogTabId(viewToTab(section.view)),
      panelId: catalogTabPanelId(viewToTab(section.view)),
      board: false,
    })),
    ...boards.map((board) => ({
      view: boardCanvasView(board.id),
      label: board.title,
      glyph: '◪',
      tabId: boardTabId(board.id),
      panelId: boardTabPanelId(board.id),
      board: true,
    })),
  ];

  // A view can be active without being on the bar (a deep link to an unpinned
  // board, or a just-removed core view) — no tab is selected then, and the
  // roving tabindex parks on the first tab so the tablist stays reachable.
  const hasSelection = entries.some((entry) => entry.view === activeView);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const idx = entries.findIndex((entry) => entry.view === activeView);
    let nextIdx: number;
    if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = entries.length - 1;
    else {
      const delta = e.key === 'ArrowRight' ? 1 : -1;
      nextIdx = (idx + delta + entries.length) % entries.length;
    }
    const next = entries[nextIdx];
    onNavigate(next.view);
    btnRefs.current[next.view]?.focus();
  };

  const firstBoardView = boards.length > 0 ? boardCanvasView(boards[0].id) : null;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'stretch',
        gap: 2,
        // SC-2929 — the bar is user-growable since the View Library; past the
        // column width it WRAPS (the pill caps at its container, the tablist
        // breaks into rows). Wrap over horizontal scroll deliberately: the
        // complaint was "can't see what I have", and scrolled-away tabs are
        // hidden by definition.
        maxWidth: '100%',
        background: t.surfaceAlt,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: 3,
      }}
    >
      <div
        role="tablist"
        aria-orientation="horizontal"
        aria-label="Catalog views"
        onKeyDown={onKeyDown}
        // `minWidth: 0` lets the tablist shrink below its one-row width so
        // wrapping can engage; arrow keys walk `entries` order, which IS
        // reading order across rows.
        style={{ display: 'inline-flex', alignItems: 'stretch', gap: 2, flexWrap: 'wrap', minWidth: 0 }}
      >
        {entries.map((entry, index) => {
          const selected = entry.view === activeView;
          const focusable = selected || (!hasSelection && index === 0);
          return (
            <span key={entry.view} style={{ display: 'inline-flex', alignItems: 'stretch', gap: 2 }}>
              {/* Divider between core views and the first pinned board. It
                  shares the first board tab's wrapper, so under wrap it
                  travels WITH that tab — worst case a leading tick on a new
                  row. Retires entirely with user-arranged order (SC-2930). */}
              {entry.view === firstBoardView && (
                <span
                  aria-hidden
                  style={{ width: 1, alignSelf: 'stretch', margin: '3px 4px', background: t.border }}
                />
              )}
              <button
                ref={(el) => {
                  btnRefs.current[entry.view] = el;
                }}
                id={entry.tabId}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={entry.panelId}
                tabIndex={focusable ? 0 : -1}
                onClick={() => onNavigate(entry.view)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: selected ? t.surface : 'transparent',
                  color: selected ? t.text : t.text3,
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: selected ? 600 : 500,
                  fontFamily: FONT,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    opacity: selected ? 1 : 0.7,
                    color: entry.board ? t.accent : undefined,
                  }}
                >
                  {entry.glyph}
                </span>
                <span>{entry.label}</span>
              </button>
            </span>
          );
        })}
      </div>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label="Add views from the library"
        title="Add views from the library"
        onClick={onOpenLibrary}
        style={{
          background: 'transparent',
          color: t.text3,
          border: 'none',
          borderRadius: 6,
          padding: '6px 10px',
          fontSize: 14,
          fontFamily: FONT,
          cursor: 'pointer',
          // Rides the LAST row when the tablist wraps (it reads as "add
          // another" at the end of the list, not a floating control).
          alignSelf: 'flex-end',
        }}
      >
        +
      </button>
    </div>
  );
}
